import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
  MobileMenu?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  MobileMenu,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative h-[48px] items-center">
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
            <div
              className={classNames(
                'mr-3 inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {false && isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
              <div className="ml-1">
                {<Icons.OHIFLogo />}
              </div>
            </div>
          </div>
          <div className="absolute top-1/2 left-[250px] h-8 -translate-y-1/2">{Secondary}</div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            <div className="flex items-center justify-center space-x-2">{children}</div>
          </div>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center">
            <div className="hidden md:flex items-center">
              {UndoRedo}
              <div className="border-muted mx-1.5 h-[25px] border-r"></div>
              {PatientInfo}
              <div className="border-muted mx-1.5 h-[25px] border-r"></div>
              <div className="flex-shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-primary hover:bg-muted mt-2 h-full w-full"
                    >
                      <Icons.GearSettings />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {menuOptions.map((option, index) => {
                      const IconComponent = option.icon
                        ? Icons[option.icon as keyof typeof Icons]
                        : null;
                      return (
                        <DropdownMenuItem
                          key={index}
                          onSelect={option.onClick}
                          className="flex items-center gap-2 py-2"
                        >
                          {IconComponent && (
                            <span className="flex h-4 w-4 items-center justify-center">
                              <Icons.ByName name={option.icon} />
                            </span>
                          )}
                          <span className="flex-1">{option.title}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div className="md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-primary hover:bg-primary-dark"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  <Icons.HamburgerMenu className="h-6 w-6" />
                </Button>
            </div>
          </div>
           {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-[340px] bg-black border border-gray-700 rounded-lg shadow-lg z-50 md:hidden">
              <div className="p-6">
                <div className="text-white text-base font-semibold mb-4">Tools</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {MobileMenu}
                </div>
                <div className="border-t border-gray-700 pt-4">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          option.onClick();
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-2 py-3 text-white hover:bg-gray-800 rounded text-base"
                      >
                        {IconComponent && (
                          <span className="flex h-5 w-5 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                          </span>
                        )}
                        <span>{option.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
